import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useStory, STEPS } from '../../context/StoryContext';
import { api } from '../../services/api';
import { Sparkles } from 'lucide-react';
import BackArrow from '../ui/BackArrow';

const TURRIS_LOGO = '/logo-turris.svg';
const AVATAR_NOI = '/avatar-noi.png';
const AVATAR_NOIA = '/avatar-noia.png';

export default function NameScreen() {
  const { 
    goToStep, goBack, language, protagonistType, uiTexts, 
    protagonistName, setProtagonistName, setStoryData, setError 
  } = useStory();
  const [localName, setLocalName] = useState(protagonistName);
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // Local loading state

  const validateName = (name) => {
    if (!name || name.trim().length === 0) {
      return 'El nom és obligatori';
    }
    if (name.trim().length < 2) {
      return 'El nom ha de tenir almenys 2 caràcters';
    }
    if (name.trim().length > 11) {
      return 'El nom no pot tenir més de 11 caràcters';
    }
    const nameRegex = /^[a-zA-ZÀ-ÿñÑçÇ\s]+$/;
    if (!nameRegex.test(name.trim())) {
      return 'El nom només pot contenir lletres';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedName = localName.trim();
    const error = validateName(trimmedName);
    if (error) {
      setValidationError(error);
      return;
    }

    setIsSubmitting(true);
    setValidationError('');
    
    try {
      setProtagonistName(trimmedName);
      
      const preview = await api.getStoryPreview(language, trimmedName, protagonistType);
      setStoryData(preview);
      
      api.trackEvent('name_entered', { 
        protagonist_name: trimmedName,
        protagonist_type: protagonistType
      });
      
      goToStep(STEPS.COVER);
    } catch (error) {
      console.error('Error getting story preview:', error);
      // Check if it's a moderation error (400 status)
      const errorResponse = error.response;
      if (errorResponse && errorResponse.status === 400) {
        const detail = errorResponse.data?.detail || "Si us plau, escriu un nom adequat per al conte";
        setValidationError(detail);
      } else {
        setError('Error al carregar el conte. Si us plau, torna-ho a provar.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    // Limit to 11 characters
    if (value.length <= 11) {
      setLocalName(value);
    }
    if (validationError) {
      setValidationError('');
    }
  };

  const exampleName = protagonistType === 'noi' ? 'Max' : 'Noa';

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FDFBF7' }}
    >
      {/* Header */}
      <header className="p-4 sm:p-6 flex items-center justify-between">
        <BackArrow onClick={goBack} />
        <img src={TURRIS_LOGO} alt="Turris" className="h-8 sm:h-10" />
        <div className="w-10" />
      </header>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 py-2">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#2A9D8F' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#D32F2F' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#E0E0E0' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#E0E0E0' }} />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <motion.img 
          src={protagonistType === 'noi' ? AVATAR_NOI : AVATAR_NOIA}
          alt={protagonistType === 'noi' ? 'Protagonista noi' : 'Protagonista noia'}
          className="w-32 h-32 sm:w-40 sm:h-40 mb-6 object-contain"
          style={{ 
            filter: 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.25))'
          }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
        />

        <motion.h1
          className="font-heading text-2xl sm:text-3xl font-bold text-center mb-2"
          style={{ color: '#1a365d' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Com es diu {protagonistType === 'noi' ? 'el' : 'la'} protagonista?
        </motion.h1>

        <motion.p
          className="text-sm mb-8 text-center"
          style={{ color: '#5C5C5C' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Exemple: {exampleName}
        </motion.p>

        <motion.form
          onSubmit={handleSubmit}
          className="w-full max-w-md flex flex-col items-center gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="w-full">
            <input
              data-testid="name-input"
              type="text"
              value={localName}
              onChange={handleNameChange}
              placeholder="Escriu el nom aquí"
              className="w-full text-center"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '2rem',
                fontWeight: 600,
                border: 'none',
                borderBottom: `3px solid ${validationError ? '#D32F2F' : '#E0E0E0'}`,
                background: 'transparent',
                padding: '16px',
                color: '#2B2B2B',
                outline: 'none'
              }}
              autoComplete="off"
            />
            {validationError && (
              <p
                className="text-center mt-3 text-sm"
                style={{ color: '#D32F2F' }}
                data-testid="validation-error"
              >
                {validationError}
              </p>
            )}
          </div>

          <button
            data-testid="view-story-button"
            type="submit"
            className="px-10 py-4 rounded-full text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 flex items-center gap-2"
            style={{ 
              backgroundColor: '#D32F2F',
              boxShadow: '0 8px 24px rgba(211, 47, 47, 0.25)'
            }}
          >
            <Sparkles className="w-5 h-5" />
            Veure el meu conte
          </button>
        </motion.form>
      </main>
    </div>
  );
}
